import * as React from 'react'

import { useSocialShareDialog } from '../../../contexts/social-share-dialog-context'
import type { ShareOption } from '../models/share-options'
import { SHARE_OPTIONS } from '../models/share-options'
import { CopyIcon } from '../../icons'
import { useAskCfTranslation } from '../../../contexts/ask-cf-translation-context'
import { Button } from '@repo/design-system-cf/components/button'
import { CloseIcon } from 'primitives/icons/close'
import { tw } from 'utils/classnames'

export function SocialShareDialogBody({ shareUrl }: { shareUrl: string }) {
  const { getTranslations } = useAskCfTranslation()
  const t = getTranslations('social_share_dialog')
  const { closeDialog, dialogHeaderText } = useSocialShareDialog()

  return (
    <section className="rounded-t-[12px] md:rounded-[12px] bg-cf-white border border-[#1313151F] relative w-full max-w-[880px] p-5 md:p-8">
      <button className="absolute top-3 right-3 " onClick={() => closeDialog()}>
        <CloseIcon className="text-cf-grey-600" />
      </button>
      <header className="mb-5">
        <h2 className="text-ask-ui-text-secondary text-[28px] font-bold">
          {dialogHeaderText || t('title')}
        </h2>
      </header>
      <main>
        <div className="flex flex-wrap gap-4">
          {SHARE_OPTIONS.map(shareOption => (
            <ShareButton key={shareOption.label} shareOption={shareOption} />
          ))}
        </div>
        <ReadonlyCopyTextbox text={shareUrl} />
      </main>
      <div className="flex justify-center md:justify-end mt-8">
        <Button className="w-full md:w-auto" variant={'ghost'} onClick={() => closeDialog()}>
          {t('cancel')}
        </Button>
      </div>
    </section>
  )
}

function ShareButton({ shareOption }: { shareOption: ShareOption }) {
  const { shareUrl } = useSocialShareDialog()
  const encodedShareUrl = encodeURIComponent(shareUrl!)
  const href = shareOption.getHref(encodedShareUrl)

  return (
    <Button variant={'secondary'} className=" py-2 px-3">
      <a href={href} className="flex justify-start items-center gap-2">
        <div>{shareOption.icon}</div>
        <span className="text-16 font-semibold">{shareOption.label}</span>
      </a>
    </Button>
  )
}

function ReadonlyCopyTextbox({ text }: { text: string }) {
  const { getTranslations } = useAskCfTranslation()
  const t = getTranslations('social_share_dialog')
  const [isCopied, setIsCopied] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  function copyToClipboard() {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true)
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
      }
      hideTimerRef.current = setTimeout(() => setIsCopied(false), 3000)
    })
  }

  React.useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
      }
    }
  }, [])

  return (
    <div className="relative mt-6 w-full flex items-center justify-start gap-3">
      <input
        ref={inputRef}
        type="text"
        value={text}
        disabled
        readOnly
        className="bg-cf-white rounded-2 text-cf-black text-16 border-[#1313151F] h-[48px] w-full border p-4 font-normal"
      />

      <div className="relative hidden md:block">
        <div
          role="status"
          className={tw(
            'pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-16 transition-all duration-300',
            isCopied ? 'opacity-100 translate-y-0 ease-in' : 'opacity-0 translate-y-4 ease-out',
          )}
        >
          <div className="relative rounded-[6px] bg-cf-grey-300 text-12 text-cf-grey-1000 px-3 py-1 w-fit whitespace-nowrap">
            {t('link_copied')}
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 bg-cf-grey-300 rotate-45" />
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={copyToClipboard}
          size="large"
          aria-label={t('copy_link')}
          iconLeft={<CopyIcon />}
        >
          {t('copy_link')}
        </Button>
      </div>

      {/* Mobile: icon-only button + the same tooltip */}
      <div className="relative block md:hidden">
        <Button
          variant="secondary"
          onClick={copyToClipboard}
          size="large"
          aria-label="Copy link"
          iconLeft={<CopyIcon />}
        />
        <div
          role="status"
          aria-live="polite"
          className={tw(
            'pointer-events-none absolute right-0 -translate-x-0 bottom-[calc(100%+10px)] transition-all duration-300',
            isCopied ? 'opacity-100 translate-y-0 ease-in' : 'opacity-0 translate-y-4 ease-out',
          )}
        >
          <div className="relative rounded-[6px] bg-cf-grey-300 text-12 text-cf-grey-1000 px-3 py-1 w-fit whitespace-nowrap">
            {t('link_copied')}
            <span className="absolute right-1 -translate-x-1/2 -bottom-0.5 w-3 h-3 bg-cf-grey-300 rotate-45" />
          </div>
        </div>
      </div>
    </div>
  )
}
